/**
 * Beauty Gables — Shopify Theme JavaScript
 * Version: 1.0.0
 */

'use strict';

/* ============================================
   UTILITIES
   ============================================ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const fmt = window.beautyGablesConfig?.shopMoneyFormat || '${{amount}}';
  return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.floor(cents / 100));
}

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function fetchJSON(url, opts = {}) {
  return fetch(url, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    ...opts,
  }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

/* ============================================
   CART STORE
   ============================================ */
const Cart = {
  async get() {
    return fetchJSON('/cart.js');
  },
  async add(variantId, qty = 1, properties = {}) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: variantId, quantity: qty, properties }),
    });
  },
  async change(key, qty) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: qty }),
    });
  },
  async remove(key) {
    return this.change(key, 0);
  },
};

/* ============================================
   CART DRAWER
   ============================================ */
const CartDrawer = (() => {
  const drawer = $('#CartDrawer');
  const overlay = $('#CartOverlay');
  const closeBtn = $('#CartClose');
  const cartEmptyCTA = $('#CartEmptyCTA');

  if (!drawer) return { open() {}, close() {}, update() {} };

  function open() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
  }

  function close() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  async function refresh() {
    const cart = await Cart.get();
    renderItems(cart);
    updateCount(cart.item_count);
    updateTotal(cart.total_price);
    updateShippingBar(cart.total_price);
  }

  function updateCount(count) {
    $$('#CartCount').forEach(el => { el.textContent = count; });
  }

  function updateTotal(cents) {
    $$('#CartTotal, #CartPageTotal, #CartPageTotalFinal').forEach(el => {
      el.textContent = formatMoney(cents);
    });
  }

  function updateShippingBar(totalCents) {
    const threshold = (window.beautyGablesConfig?.freeShippingThreshold || 50) * 100;
    const bar = drawer.querySelector('.cart-drawer__shipping-fill');
    if (bar && threshold > 0) {
      const pct = Math.min((totalCents / threshold) * 100, 100);
      bar.style.width = `${pct}%`;
    }
  }

  function renderItems(cart) {
    const body = $('#CartDrawerBody');
    const footer = drawer.querySelector('.cart-drawer__footer');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty" id="CartEmpty">
          <svg class="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p class="cart-drawer__empty-title">Your cart is empty</p>
          <button class="btn btn--primary" id="CartEmptyCTA">Continue Shopping</button>
        </div>`;
      if (footer) footer.style.display = 'none';
      const cta = $('#CartEmptyCTA');
      if (cta) cta.addEventListener('click', close);
    } else {
      body.innerHTML = `<ul class="cart-drawer__items" id="CartItems">
        ${cart.items.map(item => `
          <li class="cart-item" data-key="${item.key}">
            <div class="cart-item__image-wrap">
              <a href="${item.url}">
                ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy" class="cart-item__image">` : ''}
              </a>
            </div>
            <div class="cart-item__details">
              ${item.vendor ? `<p class="cart-item__vendor">${escapeHtml(item.vendor)}</p>` : ''}
              <a href="${item.url}" class="cart-item__name">${escapeHtml(item.product_title)}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
              <div class="cart-item__bottom">
                <div class="cart-item__qty">
                  <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                  <span class="qty-number" id="qty-${item.key}">${item.quantity}</span>
                  <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                </div>
                <span class="cart-item__price">${formatMoney(item.line_price)}</span>
              </div>
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </li>`).join('')}
      </ul>`;
      if (footer) {
        footer.style.display = '';
        const checkoutBtn = footer.querySelector('.cart-drawer__checkout');
        if (checkoutBtn) checkoutBtn.href = '/checkout';
      }
      bindCartItemActions();
    }

    const countEl = drawer.querySelector('.cart-drawer__count');
    if (countEl) countEl.textContent = `(${cart.item_count})`;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  function bindCartItemActions() {
    $$('.qty-btn', drawer).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const qtyEl = $(`#qty-${key}`, drawer) || $(`#qty-${key}`);
        let currentQty = parseInt(qtyEl?.textContent || '1');
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        btn.disabled = true;
        try {
          await Cart.change(key, newQty);
          await refresh();
        } finally {
          btn.disabled = false;
        }
      });
    });

    $$('.cart-item__remove', drawer).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const item = btn.closest('.cart-item');
        if (item) { item.style.opacity = '.4'; item.style.pointerEvents = 'none'; }
        try {
          await Cart.remove(key);
          await refresh();
        } catch {
          if (item) { item.style.opacity = ''; item.style.pointerEvents = ''; }
        }
      });
    });
  }

  // Direct checkout handler — bypasses page transition completely
  drawer.addEventListener('click', e => {
    const checkoutBtn = e.target.closest('.cart-drawer__checkout');
    if (!checkoutBtn) return;
    e.preventDefault();
    e.stopPropagation();
    window.location.href = '/checkout';
  });

  // Events
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  cartEmptyCTA?.addEventListener('click', close);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // Cart toggle — event delegation covers BOTH mobile and desktop buttons
  // (Both share class js-cart-toggle; using delegation avoids duplicate-ID bug)
  document.addEventListener('click', e => {
    if (e.target.closest('.js-cart-toggle')) {
      if (window.beautyGablesConfig?.cartType === 'drawer') {
        open();
      } else {
        window.location.href = '/cart';
      }
    }
  });

  // Cart page qty buttons
  $$(`.cart-item__qty .qty-btn`).forEach(btn => {
    if (!drawer.contains(btn)) {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const qtyEl = $(`#qty-${key}`);
        let currentQty = parseInt(qtyEl?.textContent || '1');
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        await Cart.change(key, newQty);
        window.location.reload();
      });
    }
  });

  // Cart page remove
  $$('.cart-item__remove:not(#CartDrawer .cart-item__remove)').forEach(btn => {
    btn.addEventListener('click', async () => {
      await Cart.remove(btn.dataset.key);
      window.location.reload();
    });
  });

  return { open, close, refresh };
})();

/* ============================================
   ADD TO CART
   ============================================ */
async function addToCart(variantId, btn, qty = 1) {
  btn.classList.add('is-loading');
  btn.disabled = true;

  try {
    await Cart.add(variantId, qty);

    if (window.beautyGablesConfig?.cartType === 'drawer') {
      await CartDrawer.refresh();
      CartDrawer.open();
    }

    // Success animation
    const card = btn.closest('.product-card');
    if (card) {
      card.classList.add('product-card--added');
      setTimeout(() => card.classList.remove('product-card--added'), 2000);
    }

    // Product page success
    const label = btn.querySelector('.product-form__atc-label');
    if (label) {
      const originalText = label.textContent;
      label.textContent = '✓ Added to Cart!';
      btn.style.background = '#10b981';
      setTimeout(() => {
        label.textContent = originalText;
        btn.style.background = '';
      }, 2200);
    }

  } catch (err) {
    console.error('Add to cart failed:', err);
    const label = btn.querySelector('.product-form__atc-label, .product-card__add-label');
    if (label) {
      const originalText = label.textContent;
      label.textContent = 'Error — try again';
      btn.style.background = '#ef4444';
      setTimeout(() => { label.textContent = originalText; btn.style.background = ''; }, 2000);
    }
  } finally {
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
}

// Product card "Add to Cart" buttons
document.addEventListener('click', e => {
  const btn = e.target.closest('.product-card__add-to-cart[data-variant-id]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  addToCart(btn.dataset.variantId, btn);
});

// Product page form
const productForm = $('#ProductForm');
if (productForm) {
  productForm.addEventListener('submit', e => {
    e.preventDefault();
    const variantInput = productForm.querySelector('[name="id"]');
    const qtyInput = productForm.querySelector('[name="quantity"]');
    const submitBtn = productForm.querySelector('[type="submit"]');
    if (!variantInput || !submitBtn) return;
    const variantId = variantInput.value;
    const qty = parseInt(qtyInput?.value || '1');
    addToCart(variantId, submitBtn, qty);
  });
}

/* ============================================
   PRODUCT GALLERY
   ============================================ */
const productGallery = (() => {
  const mainImg = $('#ProductMainImg');
  const thumbs = $$('.product-gallery__thumb');

  if (!mainImg || !thumbs.length) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.imgSrc;
      const alt = thumb.dataset.imgAlt;
      mainImg.style.opacity = '0';
      setTimeout(() => {
        mainImg.src = src;
        mainImg.alt = alt;
        mainImg.style.opacity = '1';
      }, 200);
      thumbs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-selected', 'true');
    });
    thumb.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); thumb.click(); }
    });
  });
})();

/* ============================================
   PRODUCT TABS
   ============================================ */
const productTabs = (() => {
  const tabNav = $('#ProductTabs');
  if (!tabNav) return;

  const buttons = $$('.product-tabs__btn', tabNav);
  const panels = $$('.product-tabs__panel', tabNav);

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      buttons.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => p.classList.remove('is-active'));
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      const panel = $(`#${btn.getAttribute('aria-controls')}`);
      if (panel) panel.classList.add('is-active');
    });
  });
})();

/* ============================================
   PRODUCT QUANTITY CONTROLS
   ============================================ */
const qtyControls = (() => {
  const increaseBtn = $('#QtyIncrease');
  const decreaseBtn = $('#QtyDecrease');
  const qtyInput = $('#ProductQty');
  if (!increaseBtn || !decreaseBtn || !qtyInput) return;

  increaseBtn.addEventListener('click', () => {
    qtyInput.value = parseInt(qtyInput.value) + 1;
  });
  decreaseBtn.addEventListener('click', () => {
    const val = parseInt(qtyInput.value);
    if (val > 1) qtyInput.value = val - 1;
  });
})();

/* ============================================
   VARIANT SELECTOR
   ============================================ */
const variantSelector = (() => {
  const optionBtns = $$('.product-form__option-btn');
  const variantInput = $('#ProductVariantId');
  const atcBtn = $('#AddToCartBtn');
  const priceEl = $('.product-details__price');

  if (!optionBtns.length || !variantInput) return;

  const productData = window.__productData;

  optionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const pos = btn.dataset.optionPosition;
      const siblings = $$(`.product-form__option-btn[data-option-position="${pos}"]`);
      siblings.forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');

      const labelValue = $(`#OptionValue-${pos}`);
      if (labelValue) labelValue.textContent = btn.dataset.value;
    });
  });
})();

/* ============================================
   PRODUCT RECOMMENDATIONS (AJAX)
   ============================================ */
const productRecs = (() => {
  const section = document.querySelector('[id^="ProductRecommendations-"]');
  if (!section) return;

  const productId = section.dataset.productId;
  const sectionId = section.dataset.sectionId;
  const limit     = section.dataset.limit || 4;
  const grid      = section.querySelector('#ProductRecsGrid');
  if (!productId || !sectionId || !grid) return;

  const url = `/recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}`;

  fetch(url)
    .then(r => {
      if (!r.ok) throw new Error('fetch failed');
      return r.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc    = parser.parseFromString(html, 'text/html');
      /* Shopify wraps the section in a div — grab cards inside the recs grid */
      const cards  = doc.querySelectorAll('.product-card');
      if (cards.length === 0) {
        section.style.display = 'none';
        return;
      }
      grid.innerHTML = '';
      cards.forEach(card => grid.appendChild(card));
      /* Re-attach quick-view / quick-add events for newly inserted cards */
      if (typeof initProductCardEvents === 'function') initProductCardEvents(grid);
    })
    .catch(() => { section.style.display = 'none'; });
})();


/* ============================================
   SLIDESHOW
   ============================================ */
document.querySelectorAll('[id^="Slideshow-"]').forEach(slideshow => {
  const id = slideshow.id.replace('Slideshow-', '');
  const slides = $$('.slideshow__slide', slideshow);
  const dots = $$('.slideshow__dot', slideshow);
  const prevBtn = $(`#SlideshowPrev-${id}`);
  const nextBtn = $(`#SlideshowNext-${id}`);
  if (slides.length <= 1) return;

  let current = 0;
  let autoplay = null;
  const autoplayDelay = parseInt(slideshow.closest('[data-autoplay]')?.dataset.autoplay || '6000');

  function goTo(idx, direction = 'next') {
    slides[current].classList.remove('is-active');
    dots[current]?.classList.remove('is-active');
    dots[current]?.setAttribute('aria-selected', 'false');
    current = (idx + slides.length) % slides.length;
    slides[current].classList.add('is-active');
    dots[current]?.classList.add('is-active');
    dots[current]?.setAttribute('aria-selected', 'true');
  }

  function startAutoplay() {
    if (!autoplayDelay || autoplayDelay === 0) return;
    autoplay = setInterval(() => goTo(current + 1), autoplayDelay);
  }

  function stopAutoplay() { clearInterval(autoplay); }

  nextBtn?.addEventListener('click', () => { stopAutoplay(); goTo(current + 1); startAutoplay(); });
  prevBtn?.addEventListener('click', () => { stopAutoplay(); goTo(current - 1); startAutoplay(); });

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => { stopAutoplay(); goTo(i); startAutoplay(); });
  });

  // Touch swipe
  let touchStartX = 0;
  slideshow.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
  slideshow.addEventListener('touchend', e => {
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { stopAutoplay(); goTo(diff > 0 ? current + 1 : current - 1); startAutoplay(); }
  });

  startAutoplay();
});

/* ============================================
   HEADER SCROLL BEHAVIOR
   ============================================ */
const siteHeader = $('#SiteHeader');
if (siteHeader) {
  const scrollHandler = () => {
    if (window.scrollY > 20) {
      siteHeader.classList.add('is-scrolled');
    } else {
      siteHeader.classList.remove('is-scrolled');
    }
  };
  window.addEventListener('scroll', scrollHandler, { passive: true });
  scrollHandler();
}

/* ============================================
   MOBILE MENU
   ============================================ */
const mobileMenu = (() => {
  const menu = $('#MobileMenu');
  const overlay = $('#MobileMenuOverlay');
  const openBtn = $('#MobileMenuToggle');
  const closeBtn = $('#MobileMenuClose');
  if (!menu) return;

  function open() {
    menu.classList.add('is-open');
    overlay.classList.add('is-visible');
    menu.setAttribute('aria-hidden', 'false');
    openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    menu.setAttribute('aria-hidden', 'true');
    openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  openBtn?.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  $$('.mobile-menu__accordion-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const subList = btn.nextElementSibling;
      const isOpen = !subList.hidden;
      subList.hidden = isOpen;
      btn.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
  });
})();

/* ============================================
   DESKTOP NAV DROPDOWNS — click toggle
   Allows users to click the chevron button to open/close dropdowns.
   CSS :hover still works for mouse users; JS adds click/touch support.
   ============================================ */
(function() {
  // Toggle dropdown on chevron button click
  document.addEventListener('click', function(e) {
    const toggle = e.target.closest('.site-header__dropdown-toggle');
    const helpBtn = e.target.closest('.site-header__help-btn');

    if (toggle) {
      e.preventDefault();
      e.stopPropagation();
      const item = toggle.closest('.site-header__nav-item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      // Close all open nav dropdowns
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      // Toggle current
      if (!isOpen) {
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (helpBtn) {
      e.preventDefault();
      e.stopPropagation();
      const helpWrap = helpBtn.closest('.site-header__help');
      if (!helpWrap) return;
      const isOpen = helpWrap.classList.contains('is-open');
      helpWrap.classList.toggle('is-open', !isOpen);
      helpBtn.setAttribute('aria-expanded', (!isOpen).toString());
      return;
    }

    // Click outside — close all dropdowns
    if (!e.target.closest('.site-header__nav-item.has-dropdown') && !e.target.closest('.site-header__help.has-dropdown')) {
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      $$('.site-header__help.is-open').forEach(h => {
        h.classList.remove('is-open');
        const b = h.querySelector('.site-header__help-btn');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // Close on Escape
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      $$('.site-header__help.is-open').forEach(h => {
        h.classList.remove('is-open');
      });
    }
  });
})();

/* ============================================
   SEARCH DRAWER — handled by searchInit() below
   ============================================ */

/* ============================================
   PARALLAX SCROLLING
   ============================================ */
const parallaxInit = () => {
  const parallaxSections = $$('[data-parallax="true"]');
  if (!parallaxSections.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function update() {
    parallaxSections.forEach(section => {
      const rect = section.getBoundingClientRect();
      const img = section.querySelector('[data-parallax-img]');
      if (!img) return;
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!visible) return;
      const speed = 0.25;
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
      img.style.transform = `translateY(${offset}px)`;
    });
  }

  window.addEventListener('scroll', update, { passive: true });
  update();
};

/* ============================================
   SCROLL ANIMATIONS (Intersection Observer)
   ============================================ */
const animationInit = () => {
  const elements = $$('[data-animate]');
  if (!elements.length) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const delay = entry.target.dataset.animateDelay || 0;
        setTimeout(() => entry.target.classList.add('is-visible'), delay);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
};

/* ============================================
   COLLECTION FILTERS
   ============================================ */
const collectionFilters = (() => {
  const form = $('#CollectionFiltersForm');
  const sortSelect = $('#SortBy');
  const viewBtns = $$('.collection-view-toggle__btn');
  const productGrid = $('#ProductGrid');
  const clearBtn = $('#ClearFilters');

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  if (viewBtns.length && productGrid) {
    viewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        viewBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        productGrid.dataset.view = view;
      });
    });
  }

  // Filter accordion toggles
  $$('.collection-filters__group-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      const body = btn.nextElementSibling;
      btn.setAttribute('aria-expanded', (!isExpanded).toString());
      if (body) body.style.display = isExpanded ? 'none' : '';
    });
  });

  // Filter checkboxes auto-submit
  $$('.collection-filters__checkbox').forEach(checkbox => {
    checkbox.addEventListener('change', debounce(() => {
      const url = new URL(window.location.href);
      $$('.collection-filters__checkbox').forEach(cb => {
        if (cb.checked) {
          url.searchParams.append(cb.name, cb.value);
        }
      });
      window.location.href = url.toString();
    }, 500));
  });

  clearBtn?.addEventListener('click', () => {
    window.location.href = window.location.pathname;
  });
})();

/* ============================================
   STATS COUNTER ANIMATION
   ============================================ */
const statsCounter = (() => {
  const stats = $$('.stats-bar__number[data-count]');
  if (!stats.length) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseFloat(el.dataset.count) || 0;
      const originalText = el.textContent;
      const isDecimal = originalText.includes('.');
      const suffix = originalText.replace(/[\d.,]/g, '');
      const prefix = suffix.startsWith('/') ? '' : '';

      let start = 0;
      const duration = 2000;
      const step = timestamp => {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        const value = isDecimal ? (ease * target).toFixed(1) : Math.floor(ease * target);
        el.textContent = value.toLocaleString() + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });

  stats.forEach(el => observer.observe(el));
})();

/* ============================================
   WISHLIST (local storage toggle)
   ============================================ */
const wishlistInit = () => {
  const wishlist = JSON.parse(localStorage.getItem('bg-wishlist') || '[]');

  function toggle(productId) {
    const idx = wishlist.indexOf(productId);
    if (idx >= 0) wishlist.splice(idx, 1);
    else wishlist.push(productId);
    localStorage.setItem('bg-wishlist', JSON.stringify(wishlist));
    return wishlist.includes(productId);
  }

  function syncUI() {
    $$('.product-card__wishlist, .product-form__wishlist-btn').forEach(btn => {
      const card = btn.closest('[data-product-id]');
      if (!card) return;
      const productId = card.dataset.productId;
      btn.classList.toggle('is-active', wishlist.includes(productId));
    });
  }

  document.addEventListener('click', e => {
    const btn = e.target.closest('.product-card__wishlist, .product-form__wishlist-btn');
    if (!btn) return;
    const card = btn.closest('[data-product-id]');
    if (!card) return;
    const productId = card.dataset.productId;
    const isNowActive = toggle(productId);
    btn.classList.toggle('is-active', isNowActive);
  });

  syncUI();
};

/* ============================================
   SEARCH DRAWER (simple — no predictive)
   ============================================ */
const searchInit = () => {
  const drawer  = document.getElementById('SearchDrawer');
  const overlay = document.getElementById('SearchOverlay');
  const input   = document.getElementById('SearchInput');
  const closeBtn= document.getElementById('SearchClose');

  if (!drawer) return;

  function openSearch() {
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-visible');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('no-scroll');
    setTimeout(() => input && input.focus(), 200);
  }

  function closeSearch() {
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }

  // Open — any search trigger button in the page
  document.addEventListener('click', e => {
    if (e.target.closest('#SearchToggle, .search-drawer__trigger, [id^="SearchToggle"]')) {
      openSearch();
    }
  });

  if (closeBtn) closeBtn.addEventListener('click', closeSearch);
  if (overlay)  overlay.addEventListener('click', closeSearch);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeSearch();
  });
};

/* ============================================
   INIT
   ============================================ */
document.addEventListener('DOMContentLoaded', () => {
  parallaxInit();
  animationInit();
  wishlistInit();
  searchInit();

  // Add data-animate to section headers
  $$('.section-header, .product-card, .category-card, .testimonial-card, .blog-card, .stats-bar__item').forEach((el, i) => {
    el.setAttribute('data-animate', '');
    el.setAttribute('data-animate-delay', Math.min(i % 4 * 80, 300));
  });

  // Re-run animations for newly added animate attributes
  animationInit();
});

/* ============================================
   PAGE TRANSITIONS
   ============================================ */
const pageTransitionInit = () => {
  const overlay = document.getElementById('PageTransitionOverlay');
  if (!overlay) return;

  // Intercept navigation clicks for a smooth fade-out
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Skip: external, hash, mailto, tel, javascript, target=_blank
    if (!href) return;
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    if (link.target === '_blank') return;
    if (link.hostname && link.hostname !== window.location.hostname) return;
    // Skip drawer toggles / cart links handled via JS
    if (link.dataset.action) return;
    if (link.classList.contains('site-footer__social-link')) return;
    // NEVER intercept checkout — let the browser handle it natively
    if (href.includes('/checkout') || link.classList.contains('cart-drawer__checkout') || link.name === 'checkout') return;
    // NEVER intercept links inside cart drawer or any element with data-no-transition
    if ('noTransition' in link.dataset) return;
    if (link.closest('#CartDrawer, .cart-drawer')) return;

    e.preventDefault();
    const dest = link.href;

    // Fade out
    overlay.classList.add('fade-out');
    setTimeout(() => {
      window.location.href = dest;
    }, 280);
  });

  // Handle browser back/forward
  window.addEventListener('pageshow', () => {
    overlay.classList.remove('fade-out');
  });
};

// Re-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  pageTransitionInit();
});

/* ============================================================
   SMART SEARCH OVERLAY
   Full-screen search with popular terms + live Shopify results
   ============================================================ */
(function() {

  const overlay    = document.getElementById('SearchOverlay');
  const backdropEl = document.getElementById('SearchOverlayBackdrop');
  const inputEl    = document.getElementById('SearchOverlayInput');
  const closeBtn   = document.getElementById('SearchOverlayClose');
  const submitLink = document.getElementById('SearchOverlaySubmit');
  const headerBar  = document.getElementById('HeaderSearchBar');
  const headerInput = document.getElementById('HeaderSearchInput');

  const popularSection  = document.getElementById('SearchPopularSection');
  const featuredSection = document.getElementById('SearchFeaturedSection');
  const resultsSection  = document.getElementById('SearchResultsSection');
  const resultsGrid     = document.getElementById('SearchResultsGrid');
  const resultsTitle    = document.getElementById('SearchResultsTitle');
  const featuredGrid    = document.getElementById('SearchFeaturedGrid');
  const seeAll          = document.getElementById('SearchSeeAll');

  if (!overlay || !inputEl) return;

  let featuredLoaded = false;

  /* ─── Open / Close ─────────────────────────────────────── */
  window.openSearchOverlay = function() {
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      inputEl.focus();
    });
    if (!featuredLoaded) loadFeaturedProducts();
    // Reset: show popular+featured, hide results
    showDefaultState();
  };

  function closeSearchOverlay() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => { overlay.hidden = true; }, 280);
    inputEl.value = '';
    showDefaultState();
  }

  function showDefaultState() {
    if (popularSection)  popularSection.hidden  = false;
    if (featuredSection) featuredSection.hidden = false;
    if (resultsSection)  resultsSection.hidden  = true;
  }

  function showResultsState() {
    if (popularSection)  popularSection.hidden  = true;
    if (featuredSection) featuredSection.hidden = true;
    if (resultsSection)  resultsSection.hidden  = false;
  }

  /* ─── Triggers ─────────────────────────────────────────── */
  if (closeBtn) closeBtn.addEventListener('click', closeSearchOverlay);
  if (backdropEl) backdropEl.addEventListener('click', closeSearchOverlay);

  // Header inline search bar click / focus
  if (headerBar)  headerBar.addEventListener('click',  window.openSearchOverlay);
  if (headerInput) headerInput.addEventListener('focus', window.openSearchOverlay);

  // Close on Escape
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && !overlay.hidden) closeSearchOverlay();
  });

  // Submit link: update href when input changes
  if (inputEl && submitLink) {
    inputEl.addEventListener('input', function() {
      if (seeAll) seeAll.href = '/search?q=' + encodeURIComponent(inputEl.value.trim()) + '&type=product';
      if (submitLink) submitLink.href = '/search?q=' + encodeURIComponent(inputEl.value.trim()) + '&type=product';
    });
    inputEl.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const q = inputEl.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
      }
    });
  }

  /* ─── Live search (debounced) ───────────────────────────── */
  const doSearch = debounce(async function(query) {
    if (!query || query.length < 2) { showDefaultState(); return; }
    showResultsState();
    resultsGrid.innerHTML = '<div class="search-overlay__loading"><span class="search-overlay__spinner"></span></div>';
    if (resultsTitle) resultsTitle.textContent = 'RESULTS FOR "' + query.toUpperCase() + '"';

    try {
      const url = '/search/suggest.json?q=' + encodeURIComponent(query)
        + '&resources[type]=product&resources[limit]=8';
      const data = await fetch(url).then(r => r.json());
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];

      if (products.length === 0) {
        resultsGrid.innerHTML = '<p class="search-overlay__no-results">No products found for "' + query + '"</p>';
        return;
      }
      resultsGrid.innerHTML = '';
      products.forEach(function(p) {
        resultsGrid.appendChild(buildSearchProductCard(p));
      });
    } catch(e) {
      resultsGrid.innerHTML = '<p class="search-overlay__no-results">Search unavailable. <a href="/search?q=' + encodeURIComponent(query) + '">Try full search →</a></p>';
    }
  }, 320);

  if (inputEl) {
    inputEl.addEventListener('input', function() {
      doSearch(inputEl.value.trim());
    });
  }

  /* ─── Build search result product card ─────────────────── */
  function buildSearchProductCard(p) {
    const href  = p.url || ('/products/' + (p.handle || ''));
    const title = p.title || '';
    const vendor = p.vendor || '';
    const price  = p.price ? formatMoney(parseInt(p.price, 10) * 100) : '';
    const img    = (p.featured_image && p.featured_image.url) ? p.featured_image.url.replace('{width}', '300') : '';

    const a = document.createElement('a');
    a.href = href;
    a.className = 'search-overlay__product-card';
    a.innerHTML =
      '<div class="search-overlay__product-img-wrap">' +
        (img ? '<img src="' + img + '" alt="' + title.replace(/"/g,'&quot;') + '" class="search-overlay__product-img" loading="lazy" width="150" height="150">' : '') +
      '</div>' +
      (vendor ? '<span class="search-overlay__product-vendor">' + vendor + '</span>' : '') +
      '<span class="search-overlay__product-title">' + title + '</span>' +
      (price ? '<span class="search-overlay__product-price">' + price + '</span>' : '');
    return a;
  }

  /* ─── Featured / popular products ──────────────────────── */
  async function loadFeaturedProducts() {
    if (!featuredGrid) return;
    featuredLoaded = true;
    try {
      const data = await fetch('/collections/all/products.json?sort_by=best-selling&limit=4').then(r => r.json());
      const products = data.products || [];
      if (products.length === 0) { featuredGrid.innerHTML = ''; return; }
      featuredGrid.innerHTML = '';
      products.forEach(function(p) {
        featuredGrid.appendChild(buildFeaturedCard(p));
      });
    } catch(e) {
      featuredGrid.innerHTML = '';
    }
  }

  function buildFeaturedCard(p) {
    const href  = '/products/' + p.handle;
    const title = p.title || '';
    const vendor = p.vendor || '';
    const variant = p.variants && p.variants[0];
    const price  = variant ? formatMoney(parseFloat(variant.price) * 100) : '';
    const img    = p.images && p.images[0] ? p.images[0].src : '';
    const imgSrc = img ? img.replace(/\.(jpg|jpeg|png|gif|webp)/, '_400x400.$1') : '';

    const a = document.createElement('a');
    a.href = href;
    a.className = 'search-overlay__product-card';
    a.innerHTML =
      '<div class="search-overlay__product-img-wrap">' +
        (imgSrc ? '<img src="' + imgSrc + '" alt="' + title.replace(/"/g,'&quot;') + '" class="search-overlay__product-img" loading="lazy" width="200" height="200">' : '') +
      '</div>' +
      (vendor ? '<span class="search-overlay__product-vendor">' + vendor + '</span>' : '') +
      '<span class="search-overlay__product-title">' + title + '</span>' +
      (price ? '<span class="search-overlay__product-price">' + price + '</span>' : '');
    return a;
  }

})();

/* ============================================================
   STICKY HEADER — visible 2 s al bajar, reaparece al subir
   ============================================================ */
(function () {
  var header = document.getElementById('SiteHeader');
  if (!header || !header.classList.contains('site-header--sticky')) return;

  var lastY      = window.scrollY;
  var hideTimer  = null;
  var headerH    = header.offsetHeight;

  function showHeader() {
    clearTimeout(hideTimer);
    header.classList.remove('site-header--hidden');
  }

  function hideAfterDelay() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      header.classList.add('site-header--hidden');
    }, 2000);
  }

  window.addEventListener('scroll', function () {
    var currentY = window.scrollY;

    // At the very top → always show, cancel any pending hide
    if (currentY <= headerH) {
      showHeader();
      lastY = currentY;
      return;
    }

    if (currentY > lastY) {
      // Scrolling DOWN: show briefly then hide
      showHeader();
      hideAfterDelay();
    } else {
      // Scrolling UP: show immediately, cancel hide
      showHeader();
      clearTimeout(hideTimer);
    }

    lastY = currentY;
  }, { passive: true });
})();


/* ============================================================
   STICKY HEADER v2 — anima el .site-header-sticky-wrap con top
   · Scroll abajo  → visible 2 s, luego sube y se oculta
   · Scroll arriba → reaparece de inmediato
   · En el tope de la página → siempre visible
   ============================================================ */
document.addEventListener('DOMContentLoaded', function () {

  var wrap   = document.querySelector('.site-header-sticky-wrap');
  if (!wrap) return;

  var lastY      = window.scrollY;
  var hideTimer  = null;
  var isHidden   = false;
  var wrapH      = 0;

  /* Calcula la altura real del wrapper (header + announcement) */
  function getWrapHeight() {
    return wrap.offsetHeight || 120;
  }

  function showWrap() {
    clearTimeout(hideTimer);
    if (!isHidden) return;
    wrap.style.top = '0px';
    isHidden = false;
  }

  function hideWrap() {
    if (isHidden) return;
    wrapH = getWrapHeight();
    wrap.style.top = '-' + wrapH + 'px';
    isHidden = true;
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideWrap, 2000);
  }

  window.addEventListener('scroll', function () {
    var currentY = window.scrollY;

    /* Al tope de la página: siempre mostrar */
    if (currentY <= 10) {
      clearTimeout(hideTimer);
      showWrap();
      lastY = currentY;
      return;
    }

    if (currentY > lastY + 4) {
      /* Scrolleando hacia abajo */
      showWrap();
      scheduleHide();
    } else if (currentY < lastY - 4) {
      /* Scrolleando hacia arriba */
      clearTimeout(hideTimer);
      showWrap();
    }

    lastY = currentY;
  }, { passive: true });

  /* Recalcular si cambia el tamano de la ventana */
  window.addEventListener('resize', function () {
    if (isHidden) {
      wrapH = getWrapHeight();
      wrap.style.top = '-' + wrapH + 'px';
    }
  });

});


/* ============================================================
   BEAUTY GABLES AI — Smart Features
   1. Social Proof (live viewers + urgency)
   2. Cart Recommendations ("Complete Your Routine")
   3. AI Chatbot Assistant
   4. Smart Bundles on product page
   ============================================================ */

/* -- 1. SOCIAL PROOF ---------------------------------------- */
(function initSocialProof() {
  // Only run on the product detail page — never on collection/card pages
  if (!document.body.classList.contains('template-product')) return;
  const productInfo = document.querySelector('.product-info__main, .product__info-wrapper, .product-info');
  if (!productInfo) return;
  const stored = JSON.parse(sessionStorage.getItem('bg_social_proof') || 'null');
  const viewers   = stored ? stored.viewers   : Math.floor(Math.random() * 13) + 4;
  const soldToday = stored ? stored.soldToday : Math.floor(Math.random() * 22) + 6;
  if (!stored) sessionStorage.setItem('bg_social_proof', JSON.stringify({ viewers, soldToday }));
  const strip = document.createElement('div');
  strip.className = 'bg-social-proof';
  strip.innerHTML =
    '<div class="bg-social-proof__item bg-social-proof__viewers">' +
      '<span class="bg-social-proof__pulse"></span>' +
      '<span><strong>' + viewers + '</strong> people viewing this now</span>' +
    '</div>' +
    '<div class="bg-social-proof__item bg-social-proof__sold">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="13" height="13"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>' +
      '<span><strong>' + soldToday + '</strong> sold in the last 24 hours</span>' +
    '</div>';
  productInfo.insertBefore(strip, productInfo.firstChild);
  let v = viewers;
  setInterval(function() {
    var delta = Math.random() < 0.5 ? 1 : -1;
    v = Math.max(2, Math.min(v + delta, 30));
    var el = strip.querySelector('.bg-social-proof__viewers strong');
    if (el) el.textContent = v;
  }, 12000);
})();

/* -- 2. CART RECOMMENDATIONS -------------------------------- */
async function loadCartRecommendations() {
  var container = document.getElementById('CartRecommendations');
  if (!container) return;
  try {
    var cart = await Cart.get();
    if (!cart.items || cart.items.length === 0) { container.style.display = 'none'; return; }
    var productId = cart.items[0].product_id;
    var res = await fetch('/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related');
    var data = await res.json();
    var products = data.products;
    if (!products || products.length === 0) { container.style.display = 'none'; return; }
    container.innerHTML =
      '<div class="cart-recs__label">Complete Your Routine</div>' +
      '<div class="cart-recs__track">' +
        products.map(function(p) {
          var v = p.variants[0];
          var img = p.images[0] ? p.images[0].src.replace(/(\.[^.]+)$/, '_160x160$1') : '';
          var price = formatMoney(v.price);
          return '<div class="cart-recs__card">' +
            '<a href="/products/' + p.handle + '" class="cart-recs__img-wrap">' +
              (img ? '<img src="' + img + '" alt="' + p.title + '" width="64" height="64" loading="lazy">' : '') +
            '</a>' +
            '<p class="cart-recs__name">' + p.title + '</p>' +
            '<p class="cart-recs__price">' + price + '</p>' +
            '<button class="cart-recs__add" data-variant="' + v.id + '" aria-label="Add to cart">+</button>' +
          '</div>';
        }).join('') +
      '</div>';
    container.style.display = '';
    container.querySelectorAll('.cart-recs__add').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        var orig = btn.textContent;
        btn.disabled = true; btn.textContent = '...';
        try {
          await Cart.add(btn.dataset.variant, 1);
          btn.textContent = '+ Added'; btn.style.background = '#10b981';
          await CartDrawer.refresh();
          await loadCartRecommendations();
          setTimeout(function() { btn.textContent = orig; btn.style.background = ''; btn.disabled = false; }, 1800);
        } catch(e) {
          btn.textContent = '!'; setTimeout(function() { btn.textContent = orig; btn.disabled = false; }, 1500);
        }
      });
    });
  } catch(e) { container.style.display = 'none'; }
}

var _origCartOpen = CartDrawer.open;
CartDrawer.open = function() { _origCartOpen.call(this); loadCartRecommendations(); };
var _origCartRefresh = CartDrawer.refresh;
CartDrawer.refresh = async function() { await _origCartRefresh.call(this); await loadCartRecommendations(); };

/* -- 3. AI CHATBOT ------------------------------------------ */
(function initChatbot() {
  var SHOP_NAME = (window.beautyGablesConfig && window.beautyGablesConfig.shopName) || 'Beauty Gables';
  /* API server base — served via the /api path proxy */
  var API_BASE = '/api';

  /* ── Non-product knowledge (shipping, returns, etc.) ── */
  var FAQ = [
    { q: /shipp|envio|envi\b/i,
      a: 'We offer free shipping on qualifying orders. Standard delivery takes 3–5 business days. You\'ll get a tracking link by email as soon as your order ships!' },
    { q: /return|refund|devolu|cambio/i,
      a: 'Returns are accepted within 30 days of purchase for unused, sealed products. Visit our Returns page or contact us and we\'ll sort it out quickly.' },
    { q: /track|donde.*order|order.*status|mi.*pedido/i,
      a: 'You can track your order using the link in your confirmation email. If you can\'t find it, contact our support team with your order number.' },
    { q: /discount|coupon|promo|sale|oferta|descuento/i,
      a: 'Subscribe to our newsletter at the bottom of the page to get exclusive discounts and early sale access!' },
    { q: /contact|help.*human|support|speak.*person|hablar.*person/i,
      a: 'Our team is happy to help! Visit our <a href="/pages/contact" style="color:var(--color-primary,#CE3872);text-decoration:underline;">Contact Us</a> page or email us — we usually reply within 1 business day.' },
    { q: /hello|hi\b|hola|hey\b|buenos|buenas|good morning|good evening/i,
      a: 'Hi there! I\'m your ' + SHOP_NAME + ' beauty assistant 💄 Ask me about products, routines, shipping, or returns — I\'m here to help!' },
    { q: /gracias|thank|thanks/i,
      a: 'You\'re welcome! Is there anything else I can help you with? 😊' },
  ];

  /* ── Concern intro messages ── */
  var INTROS = {
    dry:        'For dry or dehydrated hair I look for moisturizing & nourishing products. Let me check what we have 🔍',
    oily:       'For oily scalp I\'ll find balancing & clarifying formulas from your catalog 🔍',
    curl:       'For curls & waves I\'ll look for sulfate-free and curl-defining products 🔍',
    damaged:    'For damaged or bleached hair I\'ll search for bond-repair and protein treatments 🔍',
    color:      'For color-treated hair I\'ll find color-safe and toning options 🔍',
    volume:     'For fine hair needing volume I\'ll look for lightweight volumizing formulas 🔍',
    frizz:      'For frizzy hair I\'ll find smoothing and anti-frizz products 🔍',
    scalp:      'For scalp care I\'ll search for scalp treatments and anti-dandruff products 🔍',
    bestseller: 'Let me find our most popular products from the catalog 🔍',
    brand:      'Let me find those brand products for you 🔍',
    direct:     'Let me search your store for that right now 🔍',
  };

  /* ── Detect if it is a brand query (not a concern) ── */
  var BRANDS = ['redken','keune','joico','aquage','moroccanoil','living proof','rusk','eufora','pacinos','lagirl','la girl','seven 7','paul mitchell'];
  function detectBrand(text) {
    var t = text.toLowerCase();
    return BRANDS.find(function(b) { return t.indexOf(b) !== -1; }) || null;
  }

  /* ── Detect if it is a specific hair concern (vs a general product search) ── */
  var HAIR_REGEXES = [
    /dry.*hair|hair.*dry|hydrat|moisture|seco|resec/i,
    /oily|graso|grasa|clarif.*hair|scalp.*oily/i,
    /curl|rizado|wavy|onda/i,
    /damage|bleach|chemical|protein.*hair|quebrad|bond.*hair/i,
    /color.*treat|tint.*hair|dye.*hair|tinte|mechas|purple.*shampoo/i,
    /volume|volum|fine.*hair|thin.*hair|flat.*hair/i,
    /frizz|anti.frizz/i,
    /scalp|dandruff|caspa/i,
    /best.*seller|bestseller|mas.*vendido|top.*product/i,
  ];
  function isHairConcern(text) {
    return HAIR_REGEXES.some(function(r) { return r.test(text); });
  }

  /* ── Direct product search (non-hair, non-brand): use query words as-is ── */
  async function directCatalogSearch(query) {
    var results = [], seen = {};
    /* Build variations: full query + individual meaningful words (3+ chars) */
    var words = query.toLowerCase().replace(/[^a-z0-9 ]/g,'').split(/\s+/).filter(function(w){return w.length>2;});
    var queries = [query].concat(words.slice(0,3));
    for (var i = 0; i < queries.length && results.length < 6; i++) {
      try {
        var url = '/search/suggest.json?q=' + encodeURIComponent(queries[i]) +
          '&resources[type]=product&resources[limit]=4' +
          '&resources[options][fields]=title,vendor,product_type,tag';
        var d = await (await fetch(url)).json();
        var hits = (d.resources && d.resources.results && d.resources.results.products) || [];
        hits.forEach(function(p) { if (!seen[p.url]) { seen[p.url]=true; results.push(p); } });
      } catch(e) {}
    }
    return results.slice(0,3);
  }

  /* ── Normalize products.json → standard shape ── */
  function normalize(p) {
    var img = p.images && p.images[0] ? p.images[0].src : (p.featured_image && p.featured_image.url ? p.featured_image.url : '');
    var price = p.variants && p.variants[0]
      ? String(parseFloat(p.variants[0].price) * 100)
      : (p.price || '0');
    return {
      title: p.title,
      url: p.url || ('/products/' + (p.handle || '')),
      vendor: p.vendor || '',
      price: price,
      product_type: p.product_type || '',
      tags: p.tags || '',
      featured_image: img ? { url: img } : null
    };
  }

  /* ── STEP 1: Search Shopify catalog with multiple queries ── */
  async function catalogSearch(brandTerms, genericTerms) {
    var seen = {}, results = [];

    /* 1a: Brand-first search — most reliable (brands ARE in the store) */
    for (var i = 0; i < brandTerms.length && results.length < 12; i++) {
      try {
        var url = '/search/suggest.json?q=' + encodeURIComponent(brandTerms[i]) +
          '&resources[type]=product&resources[limit]=6' +
          '&resources[options][fields]=title,vendor,product_type,tag';
        var d = await (await fetch(url)).json();
        var hits = (d.resources && d.resources.results && d.resources.results.products) || [];
        hits.forEach(function(p) { if (!seen[p.url]) { seen[p.url] = true; results.push(p); } });
      } catch(e) {}
    }

    /* 1b: Generic terms search (product types, ingredients) */
    for (var j = 0; j < genericTerms.length && results.length < 16; j++) {
      try {
        var url2 = '/search/suggest.json?q=' + encodeURIComponent(genericTerms[j]) +
          '&resources[type]=product&resources[limit]=4' +
          '&resources[options][fields]=title,vendor,product_type,tag';
        var d2 = await (await fetch(url2)).json();
        var hits2 = (d2.resources && d2.resources.results && d2.resources.results.products) || [];
        hits2.forEach(function(p) { if (!seen[p.url]) { seen[p.url] = true; results.push(p); } });
      } catch(e) {}
    }

    /* NOTE: NO fallback to collections/all — we never want unrelated products */
    return results;
  }

  /* ── STEP 2+3: Validate via API server (knowledge base + DuckDuckGo) ── */
  async function validateProducts(query, products) {
    try {
      var resp = await fetch(API_BASE + '/chat/recommend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query, products: products }),
        signal: AbortSignal.timeout(6000)
      });
      if (!resp.ok) throw new Error('api error');
      var data = await resp.json();
      return { products: data.products || [], concern: data.concern || '', verifiedTypes: data.verifiedTypes || [] };
    } catch(e) {
      /* API unavailable — use all products, unfiltered */
      return { products: products.slice(0, 3), concern: '', verifiedTypes: [] };
    }
  }

  /* ── Get smart search terms for a concern from API ── */
  async function getSearchTerms(query) {
    try {
      var resp = await fetch(API_BASE + '/chat/search-terms?q=' + encodeURIComponent(query), { signal: AbortSignal.timeout(3000) });
      if (!resp.ok) throw new Error();
      return await resp.json();
    } catch(e) {
      return { searchTerms: [query], brands: [] };
    }
  }

  /* ── Render product cards ── */
  function appendProductCards(products, concernLabel) {
    if (!products || products.length === 0) return;
    if (concernLabel) {
      var tag = document.createElement('div');
      tag.className = 'bg-chat__concern-tag';
      tag.textContent = '✓ Verified for ' + concernLabel;
      msgs.appendChild(tag);
    }
    var wrap = document.createElement('div');
    wrap.className = 'bg-chat__products';
    wrap.innerHTML = products.map(function(p) {
      var img = p.featured_image && p.featured_image.url
        ? p.featured_image.url.replace(/(\.[^.?]+)(\?|$)/, '_160x160$1$2')
        : '';
      var price = p.price ? ('$' + (parseFloat(p.price) / 100).toFixed(2)) : '';
      var vendor = p.vendor ? ('<span class="bg-chat__prod-vendor">' + p.vendor + '</span>') : '';
      return '<a href="' + p.url + '" class="bg-chat__prod-card" target="_self">' +
        (img ? '<img src="' + img + '" alt="' + p.title + '" width="56" height="56" loading="lazy">' : '<div class="bg-chat__prod-img-placeholder"></div>') +
        '<div class="bg-chat__prod-info">' +
          vendor +
          '<span class="bg-chat__prod-name">' + p.title + '</span>' +
          (price ? '<span class="bg-chat__prod-price">' + price + '</span>' : '') +
        '</div>' +
        '<svg class="bg-chat__prod-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>' +
      '</a>';
    }).join('');
    msgs.appendChild(wrap);
    msgs.scrollTop = msgs.scrollHeight;
  }

  /* ── Main send handler ── */
  var widget = document.createElement('div');
  widget.id = 'BgChatbot';
  widget.innerHTML =
    '<button class="bg-chat__bubble" id="BgChatBubble" aria-label="Open beauty assistant">' +
      '<svg class="bg-chat__icon bg-chat__icon--chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>' +
      '<svg class="bg-chat__icon bg-chat__icon--close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '<span class="bg-chat__badge" id="BgChatBadge">1</span>' +
    '</button>' +
    '<div class="bg-chat__panel" id="BgChatPanel" aria-hidden="true">' +
      '<div class="bg-chat__head">' +
        '<div class="bg-chat__head-avatar"><svg viewBox="0 0 24 24" fill="white" width="18" height="18"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg></div>' +
        '<div class="bg-chat__head-info"><div class="bg-chat__head-name">' + SHOP_NAME + ' Assistant</div><div class="bg-chat__head-status"><span class="bg-chat__online-dot"></span>Online — replies instantly</div></div>' +
        '<button class="bg-chat__minimize" id="BgChatMinimize" aria-label="Close">&#x2715;</button>' +
      '</div>' +
      '<div class="bg-chat__messages" id="BgChatMessages">' +
        '<div class="bg-chat__msg bg-chat__msg--bot"><p>Hi! I\'m your beauty assistant 💄 Ask me about products, hair routines, shipping, or returns!</p></div>' +
        '<div class="bg-chat__quick-replies" id="BgChatQuickReplies">' +
          '<button class="bg-chat__quick" data-q="Products for dry hair">Dry hair</button>' +
          '<button class="bg-chat__quick" data-q="Products for damaged hair">Damaged hair</button>' +
          '<button class="bg-chat__quick" data-q="Show me Keune products">Keune</button>' +
          '<button class="bg-chat__quick" data-q="Show me bestsellers">Bestsellers</button>' +
        '</div>' +
      '</div>' +
      '<div class="bg-chat__compose">' +
        '<input type="text" class="bg-chat__input" id="BgChatInput" placeholder="Ask about products, routines..." autocomplete="off">' +
        '<button class="bg-chat__send" id="BgChatSend" aria-label="Send">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="17" height="17"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>' +
        '</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(widget);

  var bubble      = document.getElementById('BgChatBubble');
  var panel       = document.getElementById('BgChatPanel');
  var badge       = document.getElementById('BgChatBadge');
  var msgs        = document.getElementById('BgChatMessages');
  var inputEl     = document.getElementById('BgChatInput');
  var sendBtn     = document.getElementById('BgChatSend');
  var minBtn      = document.getElementById('BgChatMinimize');
  var quickReplies = document.getElementById('BgChatQuickReplies');
  var isOpen = false;

  function appendMsg(html, role) {
    var div = document.createElement('div');
    div.className = 'bg-chat__msg bg-chat__msg--' + role;
    div.innerHTML = '<p>' + html + '</p>';
    msgs.appendChild(div);
    msgs.scrollTop = msgs.scrollHeight;
  }

  function showTyping() {
    var div = document.createElement('div');
    div.className = 'bg-chat__msg bg-chat__msg--bot bg-chat__msg--typing';
    div.innerHTML = '<span></span><span></span><span></span>';
    msgs.appendChild(div);
    msgs.scrollTop = msgs.scrollHeight;
    return div;
  }

  function showStep(text) {
    var d = document.createElement('div');
    d.className = 'bg-chat__step';
    d.innerHTML = '<span class="bg-chat__step-dot"></span>' + text;
    msgs.appendChild(d);
    msgs.scrollTop = msgs.scrollHeight;
    return d;
  }

  async function sendMessage(text) {
    if (!text.trim()) return;
    if (quickReplies) quickReplies.style.display = 'none';
    appendMsg(text, 'user');
    inputEl.value = '';
    sendBtn.disabled = true;

    var typing = showTyping();
    await new Promise(function(r) { setTimeout(r, 500 + Math.random() * 300); });
    typing.remove();

    /* ── Check FAQ first (no product lookup needed) ── */
    var faq = FAQ.find(function(f) { return f.q.test(text); });
    if (faq) {
      appendMsg(faq.a, 'bot');
      sendBtn.disabled = false;
      return;
    }

    /* ── Product recommendation flow ── */
    var brand    = detectBrand(text);
    var isBrand  = !!brand;
    var isHair   = !isBrand && isHairConcern(text);
    var isDirect = !isBrand && !isHair; /* makeup, lipstick, tools, etc. */

    /* Show intro message */
    var introKey = isBrand ? 'brand' : (isDirect ? 'direct' : 'bestseller');
    if (isHair) {
      var q = text.toLowerCase();
      if (/dry|seco|hydrat|moisture|resec/i.test(q))               introKey = 'dry';
      else if (/oily|graso|grasa|clarif/i.test(q))                 introKey = 'oily';
      else if (/curl|rizado|wavy|onda/i.test(q))                   introKey = 'curl';
      else if (/damage|bleach|chemical|protein|quebrad|bond/i.test(q)) introKey = 'damaged';
      else if (/color.*treat|tinte|mechas|purple/i.test(q))        introKey = 'color';
      else if (/volume|volum|fine|thin|flat/i.test(q))             introKey = 'volume';
      else if (/frizz|smooth|humid/i.test(q))                      introKey = 'frizz';
      else if (/scalp|dandruff|caspa/i.test(q))                    introKey = 'scalp';
      else introKey = 'bestseller';
    }
    appendMsg(INTROS[introKey] || INTROS.direct, 'bot');

    /* ── DIRECT PATH: non-hair, non-brand queries → search as-is, no validation ── */
    if (isDirect) {
      var sd = showStep('Searching your store for "' + text + '"…');
      var directProducts = await directCatalogSearch(text);
      sd.remove();

      if (directProducts.length === 0) {
        appendMsg('I couldn\'t find "' + text + '" in your store. Try browsing our <a href="/search?q=' + encodeURIComponent(text) + '&type=product" style="color:var(--color-primary,#CE3872);text-decoration:underline;">full search results</a>!', 'bot');
      } else {
        appendProductCards(directProducts, '');
        var sa = document.createElement('a');
        sa.href = '/search?q=' + encodeURIComponent(text) + '&type=product';
        sa.className = 'bg-chat__see-all';
        sa.textContent = 'See all results →';
        msgs.appendChild(sa);
        msgs.scrollTop = msgs.scrollHeight;
      }
      sendBtn.disabled = false;
      return;
    }

    /* ── HAIR CONCERN PATH: use knowledge base + API validation ── */
    /* STEP 1: Get smart search terms (brands separated from generic) */
    var s1 = showStep('Searching your catalog…');
    var termsData = isBrand
      ? { searchTerms: [], brands: [brand] }
      : await getSearchTerms(text);
    /* brands first — they exist in the store; generic terms as secondary */
    var brandTerms   = (termsData.brands || []);
    var genericTerms = (termsData.searchTerms || [text]);
    var rawProducts  = await catalogSearch(brandTerms, genericTerms);
    s1.remove();

    if (rawProducts.length === 0) {
      appendMsg('I couldn\'t find any products in your store for that. Try browsing our <a href="/collections/all" style="color:var(--color-primary,#CE3872);text-decoration:underline;">full catalog</a>!', 'bot');
      sendBtn.disabled = false;
      return;
    }

    /* STEP 2+3: Validate via knowledge base + DuckDuckGo */
    var s2 = showStep('Verifying these products are right for you…');
    var validated = await validateProducts(text, rawProducts);
    s2.remove();

    var finalProducts = validated.products;
    var concernLabel  = validated.concern;

    /* NEVER show unvalidated products — if nothing passed, say so */
    if (!finalProducts || finalProducts.length === 0) {
      appendMsg('I found products in your store but none are specifically designed for ' + (concernLabel || 'that concern') + '. Check our <a href="/collections/all" style="color:var(--color-primary,#CE3872);text-decoration:underline;">full catalog</a> for more options!', 'bot');
      sendBtn.disabled = false;
      return;
    }

    appendProductCards(finalProducts, concernLabel);

    /* See all link */
    var seeAll = document.createElement('a');
    seeAll.href = '/search?q=' + encodeURIComponent(isBrand ? brand : (genericTerms[0] || text)) + '&type=product';
    seeAll.className = 'bg-chat__see-all';
    seeAll.textContent = 'See all results →';
    msgs.appendChild(seeAll);
    msgs.scrollTop = msgs.scrollHeight;

    sendBtn.disabled = false;
  }

  function togglePanel(open) {
    isOpen = open;
    panel.setAttribute('aria-hidden', String(!open));
    panel.classList.toggle('is-open', open);
    bubble.classList.toggle('is-active', open);
    if (open) { if (badge) badge.style.display = 'none'; setTimeout(function() { inputEl.focus(); }, 300); }
  }

  bubble.addEventListener('click', function() { togglePanel(!isOpen); });
  minBtn.addEventListener('click', function() { togglePanel(false); });
  sendBtn.addEventListener('click', function() { sendMessage(inputEl.value); });
  inputEl.addEventListener('keydown', function(e) { if (e.key === 'Enter') sendMessage(inputEl.value); });
  document.querySelectorAll('.bg-chat__quick').forEach(function(btn) {
    btn.addEventListener('click', function() { togglePanel(true); sendMessage(btn.dataset.q); });
  });

  setTimeout(function() { if (!isOpen && badge) badge.style.display = 'flex'; }, 10000);
})();

/* -- 4. SMART BUNDLES (product page) ------------------------ */
(function initSmartBundles() {
  var productId = window.beautyGablesConfig && window.beautyGablesConfig.productId;
  if (!productId) return;
  var anchor = document.querySelector('.product-info__atc-wrap, .product__purchase, #ProductForm, .product-form');
  if (!anchor) return;
  fetch('/recommendations/products.json?product_id=' + productId + '&limit=3&intent=complementary')
    .then(function(r) { return r.json(); })
    .then(function(data) {
      var products = data.products;
      if (!products || products.length < 2) return;
      var bundleProducts = products.slice(0, 2);
      var wrap = document.createElement('div');
      wrap.className = 'smart-bundle';
      wrap.innerHTML =
        '<div class="smart-bundle__label">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="13" height="13"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>' +
          ' Frequently Bought Together' +
        '</div>' +
        '<div class="smart-bundle__items">' +
          bundleProducts.map(function(p) {
            var img = p.images[0] ? p.images[0].src.replace(/(\.[^.]+)$/, '_100x100$1') : '';
            return '<label class="smart-bundle__item">' +
              '<input type="checkbox" checked data-variant="' + p.variants[0].id + '">' +
              (img ? '<img src="' + img + '" alt="' + p.title + '" width="44" height="44">' : '') +
              '<div class="smart-bundle__item-info">' +
                '<span class="smart-bundle__item-name">' + p.title + '</span>' +
                '<span class="smart-bundle__item-price">' + formatMoney(p.variants[0].price) + '</span>' +
              '</div>' +
            '</label>';
          }).join('') +
        '</div>' +
        '<button class="btn btn--outline smart-bundle__atc" id="SmartBundleAdd">Add Bundle to Cart</button>';
      anchor.after(wrap);
      document.getElementById('SmartBundleAdd').addEventListener('click', async function() {
        var btn = this;
        btn.disabled = true; btn.textContent = 'Adding...';
        var checks = wrap.querySelectorAll('input[type=checkbox]:checked');
        try {
          for (var i = 0; i < checks.length; i++) await Cart.add(checks[i].dataset.variant, 1);
          btn.textContent = '+ Added to Cart!'; btn.style.background = '#10b981'; btn.style.borderColor = '#10b981'; btn.style.color = '#fff';
          if (window.beautyGablesConfig && window.beautyGablesConfig.cartType === 'drawer') {
            await CartDrawer.refresh(); CartDrawer.open();
          }
        } catch(e) { btn.textContent = 'Error — try again'; }
        setTimeout(function() { btn.textContent = 'Add Bundle to Cart'; btn.style.background = ''; btn.style.borderColor = ''; btn.style.color = ''; btn.disabled = false; }, 2500);
      });
    })
    .catch(function() {});
})();
